import React from 'react';
import { Box, InputLabel, Theme } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import { UploadedIcon } from '@bizapp-lp/workflow/atoms/Icon';
import {
  StyledInputLabel,
  StyledHelperText,
  StyledDescription,
} from '@bizapp-lp/workflow/molecules/StyledInput';

export type FileUploaderProps = {
  className?: string;
  id?: string;
  value?: File | null | string;
  onChange?: (file: File | null) => void;
  label?: React.ReactNode;
  required?: boolean;
  error?: boolean;
  acceptMIMETypes: string[];
  helperText?: React.ReactNode;
  description?: React.ReactNode;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    label: {
      marginBottom: theme.spacing(1),
      fontSize: '14px',
      color: '#777777',
      '& span': { color: '#E91E63' },
    },
    hidden: {
      display: 'none',
    },

    inputArea: {
      height: '80px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    uploadLabel: {
      width: '100%',
      height: '100%',
      textAlign: 'center',
      lineHeight: '80px',

      color: '#0EC6BD',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',

      border: '2px dashed #C2CAD8',
      borderRadius: '3px',
      '&.drag-over': {
        borderColor: '#0EC6BD',
      },
    },

    uploaded: {
      height: '78px',
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      boxShadow: '0px 1px 3px #00000029',
      border: '1px solid #C2CAD8',
      borderRadius: '3px',
    },
    uploadedLabel: {
      fontSize: '16px',
      fontWeight: 'bold',
    },
    uploadedIcon: {
      margin: theme.spacing(0, 2, 0, 3),
    },
    closeButton: {
      position: 'absolute',
      top: '3px',
      right: '9px',
      width: '32px',
      height: '32px',
      color: '#7F7F7F',
    },
  }),
);

export const FileUploader: React.FC<FileUploaderProps> = ({
  className,
  id,
  label,
  value,
  onChange = (v) => {},
  required = false,
  error = false,
  acceptMIMETypes = [],
  helperText,
  description,
}) => {
  const classes = useStyles();
  const [clicked, setClicked] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const fileName = typeof value === 'string' ? value : value ? value.name : '';

  const handleFileChange = React.useCallback(
    (f: File | null) => {
      onChange(f);
    },
    [onChange],
  );

  const handleBlur = React.useCallback(() => {
    if (clicked) {
      setClicked(false);
    } else {
      handleFileChange(null);
    }
  }, [clicked, handleFileChange]);

  const handleClick = React.useCallback(() => {
    setClicked(true);
  }, []);

  const handleDrop = (ev: React.DragEvent<HTMLLabelElement>) => {
    const files = ev.dataTransfer.files;
    if (files.length > 0) {
      handleFileChange(files[0]);
    }
  };

  const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const files = ev.target.files;
    if (files && files.length > 0) {
      handleFileChange(files[0]);
    }
  };
  const handleRemove = () => {
    handleFileChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const overrideEventDefaults = (ev: Event) => {
    ev.preventDefault();
    ev.stopPropagation();
  };

  React.useEffect(() => {
    window.addEventListener('drop', overrideEventDefaults);
    window.addEventListener('dragover', overrideEventDefaults);
    return () => {
      window.removeEventListener('drop', overrideEventDefaults);
      window.removeEventListener('dragover', overrideEventDefaults);
    };
  }, []);

  return (
    <Box className={`${classes.root} ${className ?? ''}`}>
      {label && (
        <StyledInputLabel required={required}>{label}</StyledInputLabel>
      )}
      <input
        type="file"
        id={id}
        accept={acceptMIMETypes.join(', ')}
        multiple={false}
        required={required}
        onChange={handleChange}
        className={classes.hidden}
        ref={inputRef}
      />
      {value && value !== '' ? (
        <Box className={classes.uploaded}>
          <UploadedIcon className={classes.uploadedIcon} />
          <label htmlFor={id} tabIndex={0} className={classes.uploadedLabel}>
            {fileName}
          </label>
          <IconButton className={classes.closeButton} onClick={handleRemove}>
            <CloseIcon />
          </IconButton>
        </Box>
      ) : (
        <Box className={classes.inputArea}>
          <InputLabel
            htmlFor={id}
            tabIndex={0}
            className={classes.uploadLabel}
            onBlur={handleBlur}
            onClick={handleClick}
            onDrop={(ev: React.DragEvent<HTMLLabelElement>) => {
              ev.currentTarget.classList.remove('drag-over');
              handleDrop(ev);
            }}
            onDragEnter={(ev: React.DragEvent<HTMLLabelElement>) => {
              ev.currentTarget.classList.add('drag-over');
            }}
            onDragLeave={(ev: React.DragEvent<HTMLLabelElement>) => {
              ev.currentTarget.classList.remove('drag-over');
            }}
          >
            画像をアップロードする
          </InputLabel>
        </Box>
      )}
      {helperText && error && <StyledHelperText children={helperText} />}
      {description && <StyledDescription children={description} />}
    </Box>
  );
};
